/*
 * Copyright (C) 2015 Cadwork Informatik AG, Basel, Switzerland
 * Dual licensed under the MIT and GPL
 *
 * @author Jaroslav P�ibyl (pribyl@cadwork.cz)
 *
 */

import RenderDocument from "App/render_document";
import DirectionalLight from "App/view/lights/directional_light";
import {vec3} from "gl-matrix";
import {EBackgroundType} from "js_interface/src/modules/background/scene_background_enums";

/**
 * @param aRenderDocument
 * @extends DirectionalLight
 * @constructor
 */
export default
class SunLight extends DirectionalLight {

    public static TypeName: string = "SunLight";

    private _sunGradientImageData: ImageData = null;
    private _fogGradientImageData: ImageData = null;

    constructor(aRenderDocument: RenderDocument) {
        super(aRenderDocument);

        this._typeName = SunLight.TypeName;

        let image = new Image();
        image.onload = () => {
            let canvas = document.createElement('canvas');
            canvas.width = image.width;
            canvas.height = image.height;

            let context = canvas.getContext('2d');
            context.drawImage(image, 0, 0);

            this._sunGradientImageData = context.getImageData(0, 0, canvas.width, canvas.height);
        };

        image.src = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAABAAAAAABCAYAAABADtw1AAAABmJLR0QA/wD/AP+gvaeTAAAACXBIWXMAAA3XAAAN1wFCKJt4AAAAB3RJTUUH4QcSEAMIYCxZAgAAAopJREFUWMOtV22ywiAM3C3UmVev4/2P5r4fBEgorVp1ptqSD5LNJkVKEtzn8XgAoD0FEUQAIKiuUq3JUdvMyXpTlMmpDXe7lRW1O4L23DVshS5Av53F4LNhdA+BAGXJmQZV9qvxwqlg4qzuWx+lkja6LWb4zOCCx4QOgRd2tFzqt1isqII32YsFlpDlSgRBIUFTiMW3ABWANJgaH8gYWzeMUZcYag0tXu1xCjTa+VOvYS+qxQ9HUiLAQI81I/ry/hBqOHYFpzlaTG3XunpSx6Hlel066+n41Hk/NBFifh5PkiG2OftqvrUPsGNl02+UqkzlQS/XxUFis4CuSdXmi/mbbDuU5yTePecgxj6ehTnyX76ufXBZNRrdum7ss1Z5YcBIndihv3owMpDp+rfOYUCWd6z7PvPKwlj7YspmB5sbmPSv3LDufdtrGDfss7th4HpSrh8i3mV2hl5F3LdAxYGvkX+ely32gUgyPir0puL7Chjkri4eJzfDEoREIUHILNf+/olEIENIfCIDtuZ1YbLiL9u6v6/+EovPbLLiuzyvTrba3ithv0fyfl/1Vz6RUPTXRea/+1huN3DbgO0O/m3AfQP+7li2DbrdgJTBnICcgZzBlIGUgJRtLZlOWVcu+kwZSkXOvBYbLAAXAMl4swBK5ZdLkdeLNMLSasqTtcW9vJYuH3XDZbqNW6OMb1xntmNzzabrqHd0ZphO5kH3YG6+OIPM9+BFH/hQ9+Alevge+CT+oxMqv8ABL2rFyYmQb5wc38ljct65hPUZvkc8O8r7LIb5f5FrnDnjx7s4HPXfr7D7pg5XeugIE1ycA1d6/gxTfZiLfhjLLH9cmIWz/j+rEU9mmL7s36j7D2z1aQ1MBbP2AAAAAElFTkSuQmCC";
    }

    public readColor(aImgData: ImageData, aAngle: number): vec3 {
        if (aImgData) {
            let val = (Math.sin(aAngle) + 1.0) / 2.0;

            let x = Math.round(aImgData.width * val) - 1;
            if (x < 0) {
                x = 0;
            }
            let y = 0 * 64;

            // Now you can access pixel data from imageData.data.
            // It's a one-dimensional array of RGBA values.
            // Here's an example of how to get a pixel's color at (x,y)
            let index = (y * aImgData.width + x) * 4;
            let red = aImgData.data[index];
            let green = aImgData.data[index + 1];
            let blue = aImgData.data[index + 2];
            let alpha = aImgData.data[index + 3];

            return vec3.fromValues(red / 255, green / 255, blue / 255);
        }
        else {
            return this.diffuseColor;
        }
    }

    /**
     * Returns sun diffuse color.
     * For DAYNIGHT background type the sun color is computed from a gradient, otherwise it is white.
     *
     * @returns {vec3}
     */
    public getDiffuseColor(): vec3 {
        let color = vec3.create();
        if (this._sceneAPI.backgroundModule.getBackgroundType() === EBackgroundType.DAYNIGHT) {
            color = vec3.copy(color, this.readColor(this._sunGradientImageData, this._sceneAPI.lightingModule.getSunElevationAngle()));
        }
        else {
            color = vec3.copy(color, this.diffuseColor);
        }

        vec3.scale(color, color, this.intenzity);
        return color;
    }

    public getSpecularColor(): vec3 {
        let color = vec3.create();
        vec3.scale(color, this.specularColor, this.intenzity);
        return color;
    }
}
